import React, { forwardRef, useId } from "react";
import styles from "./Input.module.css";

type InputProps = {
  className?: string;
  inputClassName?: string;
  width?: number;

  value?: string | number;
  defaultValue?: string;
  onChange?: (value: string) => void;

  error?: string | null;
  clearable?: boolean;
  disabled?: boolean;
  onKeyDown?: React.KeyboardEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  onEnter?: () => void;

  as?: "input" | "textarea";
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value" | "defaultValue" | "className" | "width"
> &
  Omit<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    "onChange" | "value" | "defaultValue" | "className" | "width"
  >;

export const Input = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  InputProps
>((props, ref) => {
  const {
    className,
    inputClassName,
    width,
    value,
    defaultValue,
    onChange,
    error,
    clearable,
    disabled,
    onEnter,
    as = "input",
    onKeyDown,
    ...rest
  } = props;

  const errorId = useId();
  const isInvalid = Boolean(error);

  const handleKeyDown: React.KeyboardEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    if (e.key === "Enter" && onEnter && as !== "textarea") {
      onEnter();
    }
    onKeyDown?.(e);
  };

  const handleChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    onChange?.(e.target.value);
  };

  const showClear =
    clearable &&
    !disabled &&
    as !== "textarea" &&
    typeof value === "string" &&
    value.length > 0;

  const handleClear = () => {
    onChange?.("");
  };

  const isLocaleInput = ["date", "time", "datetime-local", "month", "week"].includes(
    rest.type ?? ""
  );

  const commonProps = {
    ref,
    className: [
      styles.input,
      as === "textarea" ? styles.textarea: "",
      inputClassName ?? "",
      disabled ? styles.disabled : "",
      isInvalid ? styles.errorBorder : "",
    ].join(" "),
    "aria-invalid": isInvalid,
    "aria-describedby": isInvalid ? errorId : undefined,
    disabled,
    onKeyDown: handleKeyDown,
    onChange: handleChange,
    value,
    defaultValue,
    lang: isLocaleInput ? "de" : undefined,
    ...rest,
  };

  return (
    <div
      className={`${styles.inputContainer} ${className ?? ""}`}
      style={{ width: width ? `${width}px` : undefined }}
    >
      {as === "textarea" ? (
        <textarea
          {...(commonProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
        />
      ) : (
        <div className="w-full flex flex-row">
          <input
            {...(commonProps as React.InputHTMLAttributes<HTMLInputElement>)}
          />

          {showClear && (
            <button
              type="button"
              onClick={handleClear}
              className={styles.clearButton}
              aria-label="Clear input"
            >
              ✕
            </button>
          )}
        </div>
      )}

      {isInvalid && (
        <span id={errorId} className={styles.errorText} role="alert">
          {error}
        </span>
      )}
    </div>
  );
});

Input.displayName = "Input";